import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { adminApi } from '../helpers/adminApi';
import EntryForm from './EntryForm';
import EntryList from './EntryList';

const SchemaManager = forwardRef(({ schemaKey, endpoint }, ref) => {
  const [allEntries, setAllEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  const isSingleType = ['setting', 'special-menu-link'].includes(schemaKey);

  useImperativeHandle(ref, () => ({
    resetEditingMode: () => {
      if (!isSingleType) {
        setShowForm(false);
        setEditingEntry(null);
      }
      // For single types, we don't reset as they should always be in edit mode
    }
  }));

  useEffect(() => {
    loadEntries();
  }, [endpoint]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // For single types, show form directly when data is loaded or when no data exists
    if (isSingleType && !loading) {
      if (allEntries.length > 0) {
        setEditingEntry(allEntries[0]);
        setShowForm(true);
      } else {
        setEditingEntry(null); // Create mode
        setShowForm(true);
      }
    }
  }, [allEntries, isSingleType, loading]);

  const loadEntries = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = isSingleType ? { 
        'populate': '*'
      } : { 
        'sort[0]': 'id:desc',
        'populate': '*'
      };
      
      const data = await adminApi.fetchEntries(endpoint, params);
      
      if (isSingleType) {
        // For single types, check if data has attributes or is the data itself
        const singleData = data?.data || data;
        setAllEntries(singleData ? [singleData] : []);
      } else {
        setAllEntries(data.data || []);
      }
    } catch (err) {
      setError('Erreur lors du chargement des données');
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (formData) => {
    try {
      if (isSingleType) {
        // For single types, we use PUT to create/update
        await adminApi.updateSingleType(endpoint, formData);
      } else {
        await adminApi.createEntry(endpoint, formData);
      }
      await loadEntries();
      setShowForm(false);
    } catch (err) {
      throw new Error('Erreur lors de la création');
    }
  };

  const handleUpdate = async (formData) => {
    try {
      if (isSingleType) {
        // For single types, we need to use PUT without ID
        await adminApi.updateSingleType(endpoint, formData);
      } else {
        await adminApi.updateEntry(endpoint, editingEntry.documentId, formData);
      }
      await loadEntries();
      setEditingEntry(null);
      setShowForm(false);
    } catch (err) {
      throw new Error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet élément ?')) {
      return;
    }

    try {
      await adminApi.deleteEntry(endpoint, id);
      await loadEntries();
    } catch (err) {
      setError('Erreur lors de la suppression');
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingEntry(null);
  };

  // Local filtering and pagination
  const filteredEntries = allEntries.filter(entry => {
    if (!searchTerm) return true;
    return entry.title?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const totalCount = filteredEntries.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedEntries = filteredEntries.slice(startIndex, endIndex);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
        <button 
          className="btn btn-sm btn-outline-danger ms-2"
          onClick={loadEntries}
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div>
      {!isSingleType && !showForm && (
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>
              {searchTerm ? 
                `${totalCount} résultat${totalCount !== 1 ? 's' : ''} trouvé${totalCount !== 1 ? 's' : ''} sur ${allEntries.length}` :
                `Total: ${totalCount} élément${totalCount !== 1 ? 's' : ''}`
              }
            </h5>
            <button
              className="btn btn-primary"
              onClick={() => setShowForm(true)}
            >
              Ajouter
            </button>
          </div>
          
          <div className="row mb-3">
            <div className="col-md-6">
              <input
                type="text"
                className="form-control"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <EntryForm
          schemaKey={schemaKey}
          entry={editingEntry}
          onSubmit={editingEntry ? handleUpdate : handleCreate}
          onCancel={isSingleType ? null : handleCancelForm}
        />
      )}

      {!showForm && !isSingleType && (
        <div>
          <EntryList
            schemaKey={schemaKey}
            entries={paginatedEntries}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
          
          {totalPages > 1 && (
            <nav aria-label="Pagination">
              <ul className="pagination justify-content-center">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Précédent
                  </button>
                </li>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  </li>
                ))}
                
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Suivant
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      )}
    </div>
  );
});

export default SchemaManager;